import { TFilterSelected, TOrgHeirachyTreeNode } from '@monorepo/types';
import { UTILS_GUARDS } from '../ui-filter.guards';
import { UiFilterPipeline } from '../ui-filter.pipeline';
import { TFilterGroupInfo } from '../ui-filter.types';
import { VIRTUAL_SCROLL_DEFAULT_CONFIG } from './common.model';

const findSelectedValues = (selectedFilters: TFilterSelected[], key: string | number) =>
  selectedFilters.find((filter) => filter.key === key)?.values ?? [];

const isValueSelected = (selectedValues: TFilterSelected['values'], value: string | number) =>
  selectedValues.some((selected) =>
    typeof selected === 'object' && selected !== null && 'value' in selected
      ? selected.value === value
      : selected === value,
  );

const createFilterGroupInfo = <T extends TFilterSelected>(
  item: T,
  selectedFilters: TFilterSelected[],
): TFilterGroupInfo | null => {
  if (UTILS_GUARDS.isOrgFilter(item)) {
    const selectedOrgValues = findSelectedValues(selectedFilters, item.key) as TOrgHeirachyTreeNode[];
    return {
      type: 'ORG_HIERARCHY_FILTER',
      key: item.key,
      displayName: item.displayName,
      hierarchy: item,
      filteredOrgTree: { ...item, values: selectedOrgValues },
      ...(item.version ? { version: item.version } : {}),
    };
  }

  if (UTILS_GUARDS.isMultiSelectFilter(item)) {
    const selectedValues = findSelectedValues(selectedFilters, item.key);
    const values = item.values ?? [];
    return {
      type: 'MULTI_SELECT_FILTER',
      key: item.key,
      displayName: item.displayName,
      items: values.map((val) => ({
        ...val,
        isSelected: isValueSelected(selectedValues, val.value),
      })),
      selectedValues: [],
      ...(item.version ? { version: item.version } : {}),
      ...(values.length > VIRTUAL_SCROLL_DEFAULT_CONFIG.itemSize
        ? { virtualScroll: VIRTUAL_SCROLL_DEFAULT_CONFIG }
        : {}),
    };
  }

  if (UTILS_GUARDS.isSingleSelectFilter(item)) {
    const selectedValues = findSelectedValues(selectedFilters, item.key);
    const values = item.values ?? [];
    return {
      type: 'SINGLE_SELECT_FILTER',
      key: item.key,
      displayName: item.displayName,
      items: values,
      selectedValue: values.find((val) => isValueSelected(selectedValues, val.value)),
      inputLabel: item.displayName,
    };
  }

  if (UTILS_GUARDS.isSelectFilter(item)) {
    const selectedValues = findSelectedValues(selectedFilters, item.key);
    const values = item.values ?? [];
    return {
      type: 'SELECT_FILTER',
      key: item.key,
      displayName: item.displayName,
      items: values.map((val) => ({
        value: val,
        isSelected: isValueSelected(selectedValues, val),
      })),
      selectedValues: [],
      ...(values.length > VIRTUAL_SCROLL_DEFAULT_CONFIG.itemSize
        ? { virtualScroll: VIRTUAL_SCROLL_DEFAULT_CONFIG }
        : {}),
    };
  }

  return null;
};

export const ANALYTICS_FILTER_MODEL = {
  createModel: (supportedFilters: TFilterSelected[], selectedFilters: TFilterSelected[]) => {
    const groups = supportedFilters.flatMap((item) => {
      const groupInfo = createFilterGroupInfo(item, selectedFilters);
      if (!groupInfo) {
        console.error('[ANALYTICS_FILTER_MODEL] Ignoring filter of unsupported type', item);
        return [];
      }
      return [groupInfo];
    });

    return UiFilterPipeline.computeFilterModel<TFilterSelected>({
      groups,
      hasSelectedFilters: false,
      originalData: supportedFilters,
      selectedFilters: [],
      isValid: false,
    });
  },
};
